// src/pages/ServicePages/PrivateCelebrationsPage.jsx

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactWidget from '../../components/ui/ContactWidget';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ClientLogos from 'pages/homepage/components/ClientLogos';
import PrivateCelebrationModal from '../../components/ui/PrivateCelebrationModal.jsx'; // <-- IMPORT THE NEW MODAL

// Reusable Gallery Image Component
const GalleryImage = ({ src, alt }) => (
  <div className="overflow-hidden rounded-xl group shadow-sm hover:shadow-lg transition-shadow duration-300">
    <img src={src} alt={alt} className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-500 ease-in-out" loading="lazy" />
  </div>
);

// Reusable FAQ Item Component
const FaqItem = ({ question, children }) => (
  <details className="p-6 rounded-lg bg-white border border-gray-200/80 cursor-pointer group transition-colors hover:bg-gray-50">
    <summary className="font-semibold text-lg flex items-center justify-between">{question}<Icon name="ChevronDown" className="h-5 w-5 transition-transform duration-300 group-open:rotate-180" /></summary>
    <div className="text-gray-600 mt-4 leading-relaxed">{children}</div>
  </details>
);

const PrivateCelebrationsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const galleryItems = [
    { src: '/gallery/priv1.jpg', alt: 'Chic 40th birthday party decor in Nairobi.' },
    { src: '/gallery/priv2.jpg', alt: 'An intimate anniversary dinner setup with romantic lighting.' },
    { src: '/gallery/priv3.jpg', alt: 'Elegant garden party setup for a family reunion in Kenya.' },
    { src: '/gallery/priv4.jpg', alt: 'A stylish graduation celebration with custom decor.' },
    { src: '/gallery/priv5.jpg', alt: 'Beautifully styled baby shower with a pastel theme in Nairobi.' },
    { src: '/gallery/priv6.jpg', alt: 'A joyous engagement party planned by Luxe & Allure.' },
    { src: '/gallery/priv7.jpg', alt: 'A creative and fun themed kids birthday party.' },
    { src: '/gallery/priv8.jpg', alt: 'Detailed tablescape for a private dinner party.' },
  ];
  
  // SEO: Structured Data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Luxe & Allure Private Celebration Planning",
    "image": "https://www.luxeandallureevents.co.ke/gallery/private.jpg", // Replace with absolute URL
    "@id": "https://www.luxeandallureevents.co.ke/services/private-celebrations", // Replace with absolute URL
    "url": "https://www.luxeandallureevents.co.ke/services/private-celebrations", // Replace with absolute URL
    "telephone": "+254727937010",
    "priceRange": "$$$",
    "description": "Expert private party planners in Nairobi for birthdays, anniversaries, graduations, and intimate family gatherings. We craft beautiful, personal, and stress-free celebrations.",
    "serviceType": "Party Planner",
    "areaServed": { "@type": "City", "name": "Nairobi" }
  };

  return (
    <>
      <div className="min-h-screen bg-slate-50 text-gray-800">
        <Helmet>
          <title>Private Party & Celebration Planners in Nairobi | Luxe & Allure</title>
          <meta name="description" content="Nairobi's premier private party planners. We design unforgettable birthdays, anniversaries, and family celebrations with a personal touch. Plan your perfect event with us." />
          <link rel="canonical" href="https://www.luxeandallureevents.co.ke/services/private-celebrations" />
          <meta name="keywords" content="private party planners nairobi, birthday party planners kenya, anniversary celebrations, graduation party ideas, family reunion planners, intimate event planners nairobi" />
          <meta property="og:title" content="Private Party & Celebration Planners in Nairobi | Luxe & Allure" />
          <meta property="og:description" content="We design unforgettable birthdays, anniversaries, and family celebrations with a personal touch. Plan your perfect event with us." />
          <meta property="og:image" content="https://www.luxeandallureevents.co.ke/gallery/private/milestone-birthday.jpg" />
          <meta property="og:url" content="https://www.luxeandallureevents.co.ke/services/private-celebrations" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <Header />
        <ContactWidget />

        <main className="font-sans">
          {/* --- Hero Section --- */}
          <section className="bg-white">
            <div className="container mx-auto px-6 lg:px-8 py-20 lg:py-24">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className="text-center lg:text-left">
                        <Breadcrumb />
                        <h1 className="font-serif text-5xl md:text-6xl font-bold leading-tight tracking-tight mt-4">
                            Private Celebration Planners in Nairobi
                        </h1>
                        <p className="mt-6 text-lg text-gray-600 max-w-xl mx-auto lg:mx-0">
                            We craft intimate, elegant, and joyful gatherings for life's most precious moments, allowing you to be a guest at your own celebration.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                            <Button variant="primary" size="lg" onClick={() => setIsModalOpen(true)}>
                                Plan Your Celebration
                            </Button>
                            <Button variant="outline" size="lg" onClick={() => document.getElementById('gallery-section')?.scrollIntoView({ behavior: 'smooth' })}>
                                See Our Celebrations
                                <Icon name="ArrowDown" className="ml-2 w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    <div className="h-80 lg:h-full w-full">
                        <img src="/gallery/private.jpg" alt="An elegant private celebration in Nairobi planned by Luxe & Allure." className="w-full h-full object-cover rounded-xl shadow-lg" />
                    </div>
                </div>
            </div>
          </section>

          {/* --- Philosophy Section --- */}
          <section className="py-20 lg:py-28">
            <div className="container mx-auto px-6 lg:px-8 text-center max-w-4xl">
              <h2 className="font-serif text-4xl font-bold text-gray-900">
                Celebrating You, Flawlessly
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                Life's milestones deserve to be marked with beauty, joy, and complete ease. At Luxe & Allure, we specialise in planning deeply personal and spectacular private parties. Whether it’s a milestone birthday, a landmark anniversary, or a heartwarming family reunion, our philosophy is simple: we handle every detail so you can immerse yourself in the moment and create memories with your loved ones.
              </p>
            </div>
          </section>

          {/* --- Types of Celebrations Section --- */}
          <section className="py-20 lg:py-28 bg-white">
            <div className="container mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                  <h2 className="font-serif text-4xl font-bold text-gray-900">For Every Cherished Occasion</h2>
                  <p className="mt-4 text-lg text-gray-600">Our planning services are tailored to a wide range of personal events.</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="Cake" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">Milestone Birthdays</h3>
                      <p className="text-gray-600">From chic 21sts to grand 50ths, we create birthday parties that reflect the guest of honour perfectly.</p>
                  </div>
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="Heart" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">Anniversaries</h3>
                      <p className="text-gray-600">We design romantic and memorable celebrations for landmark anniversaries, honouring your journey together.</p>
                  </div>
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="GraduationCap" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">Graduation Parties</h3>
                      <p className="text-gray-600">Celebrate academic achievements with a stylish and fun-filled party for graduates and their families.</p>
                  </div>
                  <div className="text-center p-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="Users" size={28} className="text-primary"/></div>
                      <h3 className="font-bold text-xl mb-2">Family Gatherings</h3>
                      <p className="text-gray-600">From reunions to holiday parties, we handle the logistics so you can focus on family.</p>
                  </div>
              </div>
            </div>
          </section>

          {/* --- Gallery Section --- */}
          <section id="gallery-section" className="py-20 lg:py-28">
            <div className="container mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="font-serif text-4xl font-bold text-gray-900">
                  A Portfolio of Personal Moments
                </h2>
                <p className="mt-4 text-lg text-gray-600">Glimpses of the beautiful, intimate celebrations we've had the honour of creating.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {galleryItems.map((item, index) => ( <GalleryImage key={index} src={item.src} alt={item.alt} /> ))}
              </div>
            </div>
          </section>

          {/* --- FAQ Section --- */}
          <section className="py-20 lg:py-28 bg-white">
            <div className="container mx-auto px-6 lg:px-8">
                <div className="text-center mb-16 max-w-3xl mx-auto">
                    <h2 className="font-serif text-4xl font-bold text-gray-900">Private Celebration FAQs</h2>
                    <p className="mt-4 text-lg text-gray-600">Your questions about planning a private party in Nairobi, answered.</p>
                </div>
                <div className="max-w-4xl mx-auto space-y-4">
                    <FaqItem question="What is the ideal timeline for planning a private party?">
                      <p>For most private celebrations like birthdays or anniversaries, we recommend starting the planning process 2-4 months in advance. This allows ample time for ideal venue selection, booking top-tier vendors, and developing a thoughtful design concept without feeling rushed.</p>
                    </FaqItem>
                    <FaqItem question="Do you have venue recommendations in Nairobi for private events?">
                      <p>Yes, we have a curated list of exceptional venues in and around Nairobi perfect for private events. From boutique hotels with private dining rooms to lush garden venues and modern event spaces, we can help you find the perfect backdrop for your celebration based on your guest count and desired ambiance.</p>
                    </FaqItem>
                    <FaqItem question="Can you handle all vendor communication and management?">
                      <p>Absolutely. Our full-service planning includes managing all vendors, such as caterers, decorators, photographers, and entertainers. We handle the contracts, logistics, and coordination, ensuring a cohesive team is working towards your event's success, which is a key part of what a party planner does.</p>
                    </FaqItem>
                </div>
            </div>
          </section>

          {/* --- Final Call to Action --- */}
          <section className="bg-primary text-white">
            <div className="container mx-auto px-6 lg:px-8 py-20 lg:py-24 text-center">
              <h2 className="font-serif text-4xl font-bold">
                Ready to Celebrate?
              </h2>
              <p className="mt-4 max-w-xl mx-auto text-primary/80">
                Let's start a conversation about your upcoming celebration. Contact us today to begin the stress-free journey of planning a truly memorable day.
              </p>
              <Button variant="secondary" size="xl" className="mt-8" onClick={() => setIsModalOpen(true)}>
                  Plan My Celebration
                  <Icon name="PartyPopper" className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </section>

          <ClientLogos />
        </main>
      </div>
      
      {isModalOpen && <PrivateCelebrationModal onClose={() => setIsModalOpen(false)} />}
    </>
  );
};

export default PrivateCelebrationsPage;